// libs
import { useNavigation } from "@react-navigation/native";
import React, { useEffect, useState, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TouchableNativeFeedback,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import analytics from "@services/analytics";
import { actions as rrdActions } from "@reducers/rrdReducer";
import { useDispatch } from "react-redux";
import useLocationHook from "../../components/common/LocationHook";

// assets
import BackArrow from "@assets/svg/BackArrow";

// components
import Button from "@components/Button";
import ToastMessage from "@components/Toast";

// utils
import colors from "@constants/color";
import t from "@constants/localization";
import { FULL_ORDER_RETURN_RTO } from "@constants/analyticsEventsCT";

// apis
import { getCtareRvpCXRTO, crateCreateCXRTO } from "@services/api";
import ReasonForNotDeliveredModal from "@screens/ReturnCompleteOrder/ReasonForNotDeliveredModal";
import CustomModal from "../OrdersList/CustomModal";

const { width } = Dimensions.get("screen");

const PATH = "screens/OrderDetails/index";
const ReturnCompleteOrder = ({ route }) => {
  const { params = {} } = route;
  const { goBackScreenName = "", order_id } = params;
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const getCurrentLocation = useLocationHook();

  useEffect(() => {
    setLoading(true);
    const fetchRvpCXRTO = async () => {
      try {
        if (order_id) {
          const res = await getCtareRvpCXRTO(order_id);
          setData(res.data);
        }
      } catch (e) {
        console.log(e);
      }
      setLoading(false);
    };
    fetchRvpCXRTO();
  }, [order_id]);

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const coords = await getCurrentLocation(2);
      const modalData = await crateCreateCXRTO({
        order_id,
        reason: filter || "",
        lat: coords.latitude,
        lng: coords.longitude,
      });
      analytics.trackEvent(FULL_ORDER_RETURN_RTO.DECLARATION, {
        order_id: data?.order_id,
        user_id: data?.user_id,
        user_name: data?.user_name,
      });
      dispatch(rrdActions.fetchRRDInfo());
      setModalContent(modalData?.data?.modalInfo);
      setVisible(true);
    } catch (e) {
      console.log(e);
    }
    setSubmitting(false);
  };

  const setShowModal = () => {
    setVisible(false);
    if (goBackScreenName === "PreDeliveryItemList") {
      return navigation.pop(2);
    }
    navigation.navigate("OrderDetails", {
      order_id,
      fromDeclarationSubmit: true,
    });
  };

  const { submitButtonText = " ", submitWarningText = "" } = data || {};

  const backhandler = useCallback(() => {
    if (goBackScreenName) {
      navigation.navigate(goBackScreenName, params);
    } else {
      navigation.goBack();
    }
    return true;
  }, [goBackScreenName, navigation]);

  const onDisablePress = () => {
    ToastMessage("info", submitWarningText);
  };
  if (loading) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }
  return (
    <>
      <CustomModal
        heading={modalContent?.heading}
        message={modalContent?.message}
        buttons={modalContent?.buttons}
        visible={visible}
        fontFamily={false}
        headingFontWeight="bold"
        headingFontSize={16}
        messageFontSize={14}
        setShowModal={setShowModal}
      />
      <View style={styles.header}>
        <TouchableNativeFeedback
          testID="rcco-back-button"
          style={styles.backContainer}
          onPress={backhandler}
        >
          <BackArrow style={{ margin: 16 }} />
        </TouchableNativeFeedback>
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerText}>
            {`${t(`${PATH}/order`)} - ${order_id ?? "-"}`}
          </Text>
        </View>
      </View>
      <ScrollView
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        style={styles.container}
      >
        <ReasonForNotDeliveredModal
          currentFilter={filter}
          setCurrentFilter={setFilter}
          data={data}
        />
      </ScrollView>
      <View style={styles.ctaContainer}>
        <Button
          onDisablePress={onDisablePress}
          btnText={submitButtonText}
          disabled={Object.keys(filter).length === 0}
          textColor={colors.white}
          isSubmitting={submitting}
          buttonColor={colors.primary}
          onPress={onSubmit}
          fontSize={18}
          height={50}
          borderRadius={8}
          containerStyle={{ alignSelf: "center" }}
          buttonWidth={width - 32}
          disallowClickWhileSubmitting={true}
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    backgroundColor: colors.white,
    flexDirection: "row",
    minHeight: 56,
    elevation: 8,
  },
  headerText: {
    fontSize: 16,
    color: colors.mediumBlack,
    alignSelf: "center",
    marginRight: "auto",
    fontWeight: "bold",
    marginTop: 4,
    maxWidth: "70%",
  },
  headerTextContainer: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    flex: 1,
  },
  backContainer: {
    paddingRight: 15,
    paddingVertical: 10,
  },

  ctaContainer: {
    height: 70,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.white,
    elevation: 4,
    borderTopWidth: 1,
    borderColor: colors.shadowLighter,
    shadowColor: colors.shadowLighter,
  },
});

export default ReturnCompleteOrder;
